/**
 * 중도절단 구성 — 곡선이 무엇을 못 봤는가 (DESIGN §3.3.1.1 · §8.6.2).
 *
 * **이 카드가 이 페이지의 전제다.** 관측이 끝난 이유를 세 갈래로 나눈다:
 *
 *   복구 관측    수리가 끝나는 것을 실제로 봤다            → event
 *   관측 창 끝   아직 안 끝났는데 우리 관측이 먼저 끝났다   → censored (observation_end)
 *   연계 중단    사업자→환경공단 연계가 끊겨 잘렸다        → censored (stale_truncated)
 *
 * 뒤 둘은 **원인이 다를 뿐 둘 다 "사건을 못 봤다"** 이므로 KM 처리는 같지만,
 * 개수는 반드시 나눠 보여준다 — 어느 쪽이 많은지에 따라 할 수 있는 말이 다르다.
 * 연계 중단이 대부분인 그룹의 곡선은 "이 제조사가 느리다"가 아니라 "이 제조사의
 * 데이터가 자주 끊긴다"를 보고 있는 것이다.
 *
 * **형태가 100% 누적 막대인 이유** (§8.6.2 형태 선택표: 상태 구성 → 100% 누적).
 * 절대 규모는 막대 길이가 아니라 오른쪽 숫자로 읽는다 — 구성비와 규모를 한 막대에
 * 같이 실으면 둘 다 못 읽는다.
 *
 * **상태 3색을 쓰지 않는 이유.** UP/DOWN/NA 는 충전기의 상태이고 여기 세 갈래는
 * *관측의 결말* 이다. DOWN 색을 절단에 쓰면 "연계 중단 = 고장"으로 읽힌다.
 */

import type { EChartsOption } from 'echarts'
import { useMemo } from 'react'

import { ChartFrame } from '@/components/ChartFrame'
import { EChart } from '@/components/EChart'
import { Headline } from '@/components/WidgetShell'
import { axisStyle, CHART_H, niceBand } from '@/lib/chart'
import { cssVar } from '@/lib/cssvar'
import { int, pct, pctAxis } from '@/lib/fmt'
import { useEntityLabel } from '@/lib/labels'
import type { Envelope } from '@/lib/types'
import type { CohortAxis, KMCurve, SurvivalData } from '../CohortPage'

interface Props {
  env?: Envelope<SurvivalData>
  axis: { key: CohortAxis; label: string }
  isLoading: boolean
  error: Error | null
  onRetry: () => void
}

const OBSERVATION_END = 'observation_end'
const STALE_TRUNCATED = 'stale_truncated'

/**
 * 가로축은 항상 0~100% 다 — 100% 누적 막대이므로 축이 곧 정의다.
 *
 * 상수이므로 **모듈 밖에서 한 번만 만든다.** 렌더 안에서 `niceBand(0, 1)` 을 부르면
 * 값이 같아도 매번 새 객체가 나와 아래 `option` 의 `useMemo` 가 한 번도 적중하지
 * 못한다. 그러면 렌더마다 새 option 이 나오고 `EChart` 가 인스턴스를 버리고 다시
 * 만든다 (SurvivalCurves 의 `SURVIVAL_BAND` 와 같은 이유다).
 */
const SHARE_BAND = niceBand(0, 1)

interface Row {
  id: string
  name: string
  subjects: number
  events: number
  obsEnd: number
  stale: number
  /** `right_open` 인데 사유가 비어 있는 행 — 스키마 위반이라 0 이 정상이다 */
  unknown: number
  censored: number
  withheld: boolean
  reason: string | null
}

export function CensoringPanel({ env, axis, isLoading, error, onRetry }: Props) {
  const labelOf = useEntityLabel()
  const data = env?.data
  const curves = useMemo(() => data?.curves ?? [], [data])

  const rows = useMemo<Row[]>(() => {
    const nameOf = (c: KMCurve) =>
      c.group == null ? '미상 (값 없음)' : labelOf(axis.key, c.group)
    return curves
      .map((c) => {
        const obsEnd = c.censored_by[OBSERVATION_END] ?? 0
        const stale = c.censored_by[STALE_TRUNCATED] ?? 0
        return {
          id: c.group ?? '__missing__',
          name: nameOf(c),
          subjects: c.n_subjects,
          events: c.n_events,
          obsEnd,
          stale,
          unknown: Math.max(0, c.n_censored - obsEnd - stale),
          censored: c.n_censored,
          withheld: c.verdict !== 'estimated',
          reason: c.reason,
        }
      })
      .sort((a, b) => b.subjects - a.subjects)
  }, [curves, labelOf, axis.key])

  const total = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          subjects: acc.subjects + r.subjects,
          events: acc.events + r.events,
          obsEnd: acc.obsEnd + r.obsEnd,
          stale: acc.stale + r.stale,
          unknown: acc.unknown + r.unknown,
        }),
        { subjects: 0, events: 0, obsEnd: 0, stale: 0, unknown: 0 },
      ),
    [rows],
  )
  const censoredShare = total.subjects > 0 ? (total.subjects - total.events) / total.subjects : null
  const hasUnknown = total.unknown > 0
  const notFitted = data != null && data.curve_fitted === false
  // 응답이 실제로 왔을 때만 "없다"고 말한다 — 아직 못 받은 것과 없는 것은 다르다
  const isEmpty = !isLoading && !error && data != null && rows.length === 0 && !notFitted

  const share = (n: number, of: number) => (of > 0 ? n / of : 0)
  const band = SHARE_BAND

  const option = useMemo<EChartsOption>(() => {
    const ink2 = cssVar('--c-ink-2', '#444446')
    const ink3 = cssVar('--c-ink-3', '#626266')
    const hairline = cssVar('--c-hairline', '#e0e0e0')
    // 관측의 결말이지 충전기의 상태가 아니다 — 상태 3색을 쓰지 않는 이유 (§8.6.2)
    const cEvent = cssVar('--c-cat-1', '#0066cc')
    const cStale = cssVar('--c-cat-2', '#a35200')
    const cObs = cssVar('--c-na', '#6f6f75')
    const warn = cssVar('--c-warn', '#8a6a12')

    // ECharts 카테고리축은 아래에서 위로 쌓인다 — 표본이 큰 그룹을 위에 두려면 뒤집는다
    const ordered = [...rows].reverse()
    const stack = (key: 'events' | 'obsEnd' | 'stale' | 'unknown') =>
      ordered.map((r) => share(r[key], r.subjects))

    return {
      grid: { left: 132, right: 84, top: 14, bottom: 30 },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params) => {
          const list = (Array.isArray(params) ? params : [params]) as { dataIndex?: number }[]
          const r = ordered[list[0]?.dataIndex ?? -1]
          if (!r) return ''
          return [
            `<b>${r.name}</b>${r.withheld ? ' (판정 보류)' : ''}`,
            `세션 ${int(r.subjects)}건`,
            `복구 관측 ${int(r.events)} (${pct(share(r.events, r.subjects), 1)})`,
            `관측 창 끝 ${int(r.obsEnd)} (${pct(share(r.obsEnd, r.subjects), 1)})`,
            `연계 중단 ${int(r.stale)} (${pct(share(r.stale, r.subjects), 1)})`,
            r.unknown > 0 ? `분류 안 됨 ${int(r.unknown)}` : '',
          ]
            .filter(Boolean)
            .join('<br/>')
        },
      },
      xAxis: {
        type: 'value',
        min: band.min,
        max: band.max,
        interval: band.interval,
        ...axisStyle(ink3, hairline),
        axisLabel: { fontSize: 10, color: ink3, formatter: pctAxis(band.interval * 5) },
      },
      yAxis: {
        type: 'category',
        data: ordered.map((r) => `${r.name}${r.withheld ? ' (판정 보류)' : ''}`),
        ...axisStyle(ink3, hairline),
        splitLine: { show: false },
        axisLabel: {
          fontSize: 10,
          color: ink2,
          width: 124,
          overflow: 'truncate',
        },
      },
      series: [
        {
          type: 'bar',
          name: '복구 관측',
          stack: 'end',
          barMaxWidth: 14,
          itemStyle: { color: cEvent },
          data: stack('events'),
        },
        {
          type: 'bar',
          name: '중도절단 · 관측 창 끝',
          stack: 'end',
          itemStyle: { color: cObs },
          data: stack('obsEnd'),
        },
        {
          type: 'bar',
          name: '중도절단 · 연계 중단',
          stack: 'end',
          itemStyle: { color: cStale },
          data: stack('stale'),
        },
        ...(hasUnknown
          ? [
              {
                type: 'bar' as const,
                name: '분류 안 됨',
                stack: 'end',
                itemStyle: { color: warn },
                data: stack('unknown'),
              },
            ]
          : []),
        {
          // 100% 막대는 규모를 말하지 않는다. 세션 수는 막대 밖에 직접 적는다
          type: 'scatter',
          symbolSize: 0,
          silent: true,
          data: ordered.map((_r, i) => [band.max, i]),
          label: {
            show: true,
            position: 'right',
            distance: 8,
            fontSize: 10,
            color: ink3,
            formatter: (p: { dataIndex: number }) => `${int(ordered[p.dataIndex]?.subjects)}건`,
          },
        },
      ],
    }
  }, [rows, band, hasUnknown])

  const table = (
    // 스크롤되는 상자는 키보드로도 스크롤할 수 있어야 한다 — 마우스 휠만으로 읽히는
    // 표는 키보드 사용자에게 잘린 표다 (axe scrollable-region-focusable)
    <div className="ctable" tabIndex={0} role="region" aria-label="관측 결말 구성 표">
      <table className="data-table">
        <caption className="sr-only">그룹별 관측 결말 구성</caption>
        <thead>
          <tr>
            <th scope="col">그룹</th>
            <th scope="col" className="ta-r">
              세션
            </th>
            <th scope="col" className="ta-r">
              복구 관측
            </th>
            <th scope="col" className="ta-r">
              관측 창 끝
            </th>
            <th scope="col" className="ta-r">
              연계 중단
            </th>
            {hasUnknown && (
              <th scope="col" className="ta-r">
                분류 안 됨
              </th>
            )}
            <th scope="col" className="ta-r">
              절단 비중
            </th>
            <th scope="col">곡선</th>
            <th scope="col">사유</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td>{r.name}</td>
              <td className="num ta-r">{int(r.subjects)}</td>
              <td className="num ta-r">{int(r.events)}</td>
              <td className="num ta-r">{int(r.obsEnd)}</td>
              <td className="num ta-r">{int(r.stale)}</td>
              {hasUnknown && <td className="num ta-r">{int(r.unknown)}</td>}
              <td className="num ta-r">{pct(share(r.censored, r.subjects), 1)}</td>
              <td>{r.withheld ? '판정 보류' : '그림'}</td>
              <td className="excl__why">{r.reason ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  return (
    <ChartFrame
      title="중도절단의 구성 — 무엇을 못 봤나"
      question="관측이 끝난 이유 세 갈래. 뒤 둘은 복구가 아니라 '못 본 것'이다"
      meta={env?.meta}
      headline={
        censoredShare != null ? (
          <Headline value={pct(censoredShare, 1)} unit="세션이 중도절단" tone="warn" />
        ) : undefined
      }
      legend={[
        { label: '복구 관측 (사건)', color: cssVar('--c-cat-1', '#0066cc'), pattern: 'solid' },
        {
          label: '중도절단 · 관측 창 끝',
          color: cssVar('--c-na', '#6f6f75'),
          pattern: 'solid',
        },
        {
          label: '중도절단 · 연계 중단 (STALE)',
          color: cssVar('--c-cat-2', '#a35200'),
          pattern: 'solid',
        },
        ...(hasUnknown
          ? [
              {
                label: '분류 안 됨 (사유 결측 — 스키마 위반)',
                color: cssVar('--c-warn', '#8a6a12'),
                pattern: 'solid' as const,
              },
            ]
          : []),
      ]}
      reading={`그룹마다 다운타임 세션이 어떻게 끝났는지를 100% 로 나눠 그렸습니다. 파란 몫이 실제로 복구를 관측한 세션이고, 나머지는 복구를 보지 못한 채 관측이 끝난 세션입니다. 오른쪽 숫자가 그 그룹의 세션 수이며, 막대 길이는 규모가 아니라 구성비입니다.`}
      encoding={[
        {
          role: '가로축',
          what: '세션 구성 비중 (0~100%)',
          detail:
            '모든 막대가 같은 길이인 이유는 100% 로 정규화했기 때문입니다. 구성비와 규모를 한 막대에 같이 실으면 둘 다 못 읽으므로 규모는 오른쪽 숫자로 분리했습니다.',
        },
        {
          role: '세로축',
          what: `${axis.label} · 세션 수 내림차순`,
          detail:
            '절단 비중 순으로 세우면 세션이 한두 건뿐인 그룹이 100% 로 맨 위에 옵니다 — 그건 "절단이 심하다"가 아니라 "표본이 없다"입니다. 그래서 규모 순으로 세웁니다.',
        },
        {
          role: '색',
          what: '관측의 결말 세 갈래',
          detail:
            '충전기의 상태가 아니라 관측의 결말이므로 좋고 나쁨을 뜻하는 상태색(UP/DOWN)을 쓰지 않고 비교 계열색을 씁니다. DOWN 색을 절단에 쓰면 "연계 중단 = 고장"으로 읽히는데, 연계 중단은 사업자→환경공단 전송이 끊긴 것이지 충전기 고장이 아닙니다. "관측 창 끝"만 회색인데, 그 회색은 판단이 아니라 "그 뒤를 못 봤다"는 결측의 색이라 뜻이 맞습니다.',
        },
        {
          role: '오른쪽 숫자',
          what: '그 그룹의 다운타임 세션 수',
          detail:
            '구성비만 보면 세션 2건짜리 그룹과 3,000건짜리 그룹이 같은 굵기로 보입니다. 규모를 행마다 직접 적어 그 착시를 막습니다.',
        },
        {
          role: '라벨 (판정 보류)',
          what: '곡선을 그리지 않은 그룹',
          detail:
            '사건이 적어 KM 판정을 보류한 그룹입니다. 곡선은 없지만 절단 구성은 알 수 있으므로 여기서는 빼지 않습니다 — 오히려 이런 그룹이 절단으로 가득 차 있는 경우가 많습니다.',
        },
      ]}
      isLoading={isLoading}
      error={error}
      isEmpty={isEmpty}
      onRetry={onRetry}
      table={rows.length > 0 ? table : undefined}
      notes={[
        {
          label: '종료로 세면 지표가 임계값에 붙는다.',
          text: 'STALE 로 잘린 세션을 종료로 세자 MTTR 중위값이 1,440분 — STALE 임계값 그 자체가 나왔습니다. 절단으로 처리한 뒤 71.9분이었습니다. 지표가 데이터가 아니라 우리 설정을 반영하고 있으면 그 지표는 틀린 것입니다 (§3.3.1.1).',
        },
        {
          label: '두 절단은 다른 사건이다.',
          text: '"관측 창 끝"은 우리 관측 기간이 먼저 끝난 것이고, "연계 중단"은 사업자 연계가 끊겨 그 뒤를 모르게 된 것입니다. KM 처리는 같지만 원인이 다르므로 개수를 나눠 둡니다 — 연계 중단이 대부분인 그룹의 곡선은 수리 속도가 아니라 데이터 품질을 보고 있는 것입니다.',
        },
        {
          label: '이 막대는 시간이 아니라 세션 수다.',
          text: '가동률(시간 가중)과 다른 단위입니다. 한 충전기가 여러 세션을 가질 수 있고, 세션 길이는 여기에 반영되지 않습니다.',
        },
        data?.n_invalid_duration
          ? {
              label: `KM 입력에서 뺀 세션 ${int(data.n_invalid_duration)}건.`,
              text: '길이가 결측이거나 0 이하라 생존분석에 넣을 수 없었습니다. 버리되 세어 둡니다 — "값이 없다"를 0분 사건으로 위조하지 않습니다.',
            }
          : '',
        data?.n_inconsistent_censor
          ? {
              label: `앞뒤가 맞지 않는 행 ${int(data.n_inconsistent_censor)}건.`,
              text: '종료된 세션인데 절단 사유가 붙어 있습니다. right_open 을 정본으로 삼았지만 빌드 산출물을 의심해야 합니다.',
            }
          : '',
        hasUnknown
          ? {
              label: `절단 사유가 없는 세션 ${int(total.unknown)}건.`,
              text: 'censor_reason 이 비어 있어 관측 창 끝인지 연계 중단인지 구분할 수 없습니다. 0 이어야 정상이므로 스키마 위반을 의심하세요.',
            }
          : '',
      ]}
    >
      {notFitted ? (
        <div className="verdict-note" role="status">
          <p>
            <strong>서버가 곡선을 적합하지 않아 절단 구성도 없습니다.</strong> 세션이 조회 상한에
            걸렸거나 조건에 맞는 다운타임 세션이 없습니다.
          </p>
          <p className="verdict-note__hint">
            개수만 따로 세어 보여줄 수도 있지만, 곡선과 다른 표본에서 나온 숫자를 같은 화면에 두면
            둘을 견주게 됩니다 — 그래서 함께 비웁니다.
          </p>
        </div>
      ) : (
        <>
          <div className="dstat">
            <div className="dstat__cell cens__cell--event">
              <span className="dstat__value">{int(total.events)}</span>
              <span className="dstat__label">
                복구 관측 (사건) · {pct(share(total.events, total.subjects), 1)}
              </span>
            </div>
            <div className="dstat__cell cens__cell--obs">
              <span className="dstat__value">{int(total.obsEnd)}</span>
              <span className="dstat__label">
                중도절단 · 관측 창 끝 · {pct(share(total.obsEnd, total.subjects), 1)}
              </span>
            </div>
            <div className="dstat__cell cens__cell--stale">
              <span className="dstat__value">{int(total.stale)}</span>
              <span className="dstat__label">
                중도절단 · 연계 중단 · {pct(share(total.stale, total.subjects), 1)}
              </span>
            </div>
          </div>

          <EChart
            option={option}
            height={Math.max(CHART_H, rows.length * 20 + 44)}
            ariaLabel={`그룹별 관측 결말 구성 100% 누적 막대. 그룹 ${rows.length}개. 전체 세션 ${int(
              total.subjects,
            )}건 중 복구 관측 ${int(total.events)} · 관측 창 끝 ${int(
              total.obsEnd,
            )} · 연계 중단 ${int(total.stale)}. 정확한 값은 표 보기에 있습니다.`}
          />
        </>
      )}
    </ChartFrame>
  )
}
